import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Auth } from "@supabase/auth-ui-react";
import { ThemeSupa } from "@supabase/auth-ui-shared";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { AuthError } from "@supabase/supabase-js";

const Login = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      // Redirect admin users to admin dashboard, regular users to client dashboard
      const redirectPath = isAdmin ? '/admin' : '/dashboard';
      navigate(redirectPath);
    }
  }, [user, isAdmin, navigate]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN') {
        console.log('Signed in successfully');
      } else if (event === 'SIGNED_OUT') {
        console.log('Signed out');
        toast({
          variant: "default",
          title: "Signed Out",
          description: "You have been signed out successfully.",
        });
      } else if (event === 'USER_UPDATED') {
        console.log('User updated');
      } else if (event === 'PASSWORD_RECOVERY') {
        toast({
          title: "Password Recovery",
          description: "Please check your email for password reset instructions.",
        });
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('Session token refreshed');
      }

      // Handle authentication errors
      if (!session && event === 'SIGNED_IN') {
        const error = new URLSearchParams(window.location.hash).get('error_description');
        if (error) {
          toast({
            variant: "destructive",
            title: "Authentication Error",
            description: error || "Invalid login credentials. Please check your email and password.",
          });
        }
      }
    });

    // Set up error handling for auth state changes
    const handleAuthError = (error: AuthError) => {
      let errorMessage = "An error occurred during authentication.";
      
      if (error.message.includes('Invalid login credentials')) {
        errorMessage = "Invalid email or password. Please try again.";
      } else if (error.message.includes('Email not confirmed')) {
        errorMessage = "Please verify your email address before signing in.";
      }

      toast({
        variant: "destructive",
        title: "Authentication Error",
        description: errorMessage,
      });
    };

    // Listen for auth state changes and handle errors
    const authListener = supabase.auth.onAuthStateChange((event, session) => {
      if (!session && event === 'SIGNED_IN') {
        const params = new URLSearchParams(window.location.hash.substring(1));
        const error = params.get('error');
        const errorDescription = params.get('error_description');
        
        if (error || errorDescription) {
          handleAuthError(new AuthError(errorDescription || 'Authentication failed'));
        }
      }
    });

    return () => {
      subscription.unsubscribe();
      authListener.data.subscription.unsubscribe();
    };
  }, [toast]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1A1F2C] dark:bg-[#0F1219]">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row items-center justify-center gap-8 max-w-6xl mx-auto">
          {/* Left side with image */}
          <div className="w-full md:w-1/2 flex justify-center">
            <img 
              src="/lovable-uploads/15ed641a-6822-491a-ad44-7198f7113d8f.png" 
              alt="Professional Service" 
              className="rounded-lg shadow-xl max-w-md w-full"
            />
          </div>

          {/* Right side with login form */}
          <div className="w-full md:w-1/2 max-w-md">
            <div className="glass-card p-8 rounded-lg">
              <div className="mb-8">
                <img 
                  src="/lovable-uploads/f90447f3-bb25-46e2-adea-7e5de86942c0.png" 
                  alt="Trade Co Logo" 
                  className="h-12 mx-auto mb-4 login-logo"
                />
                <h2 className="text-2xl font-semibold text-center text-white">Welcome Back</h2>
              </div>
              
              <Auth
                supabaseClient={supabase}
                appearance={{
                  theme: ThemeSupa,
                  variables: {
                    default: {
                      colors: {
                        brand: 'hsl(var(--primary))',
                        brandAccent: 'hsl(var(--primary))',
                        brandButtonText: 'white',
                        defaultButtonBackground: '#374151',
                        defaultButtonBackgroundHover: '#4B5563',
                        defaultButtonBorder: '#4B5563',
                        defaultButtonText: 'white',
                        dividerBackground: '#374151',
                        inputBackground: '#1F2937',
                        inputBorder: '#374151',
                        inputBorderHover: '#4B5563',
                        inputBorderFocus: '#6366F1',
                        inputText: 'white',
                        inputLabelText: 'white',
                        inputPlaceholder: '#9CA3AF',
                      }
                    }
                  },
                  className: {
                    container: 'text-white',
                    label: 'text-white',
                    button: 'bg-primary text-white hover:bg-primary/90',
                    input: 'bg-[#1F2937] text-white',
                    anchor: 'text-primary-foreground hover:text-primary-foreground/90',
                  }
                }}
                providers={[]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;